import React, { useState, useEffect, useRef } from 'react'
import Sidebar from '../components/chat/Sidebar'
import NewsflashBanner from '../components/newsflash/NewsflashBanner'
import TopBar from '../components/chat/TopBar'
import ChatInput from '../components/chat/ChatInput'
import MessageList from '../components/chat/MessageList'
import Welcome from '../components/chat/Welcome'
import Spinner from '../components/common/Spinner'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import { AuthStatus, useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import {
  getChatResponse,
  createCancelTokenSource,
  Company,
  DocumentReference,
  DaxQuery,
  getChat,
  GeneratedFile,
  MessageData,
  StatusUpdate,
  StreamErrorPayload,
} from '../api/chatService'
import {
  CHAT_GAILS_PERMISSION_ID,
  CHAT_TBF_PERMISSION_ID,
  hasPermission,
} from '../constants/permissions'
import { v4 as uuidv4 } from 'uuid'
import axios, { CancelTokenSource } from 'axios'

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<MessageData[]>([])
  const [isWaiting, setIsWaiting] = useState(false)
  // Live progress steps for the turn in flight, in arrival order and keyed by
  // id so a step can be flipped from active to done in place.
  const [statusSteps, setStatusSteps] = useState<StatusUpdate[]>([])
  const [chatId, setChatId] = useState<string>(uuidv4())
  const [company, setCompany] = useState<Company | null>(null)
  const [initialMessage, setInitialMessage] = useState('')
  const [focusTrigger, setFocusTrigger] = useState(0)
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { user, logout, status } = useAuth()
  const { showError } = useToast()
  const { chatId: urlChatId } = useParams()
  // True until the chat named in the URL has been fetched. On a reload we land
  // here before the chat's company is known, and rendering the composer then
  // would assert a company we have not looked up yet — which is how a reloaded
  // TBF chat sat there saying "Gail's", in red, while the backend answered as
  // TBF. Wait, the way the page already waits for auth, rather than guessing.
  const [isLoadingInitialChat, setIsLoadingInitialChat] = useState(!!urlChatId)
  const navigate = useNavigate()
  const sidebarRef = useRef<{
    removeEmptyChatIfExists: (chatId: string) => void
    reloadChats: () => void
  }>(null)

  // Upsert a step by id. An empty label means "only change the state", which is
  // how the backend closes a step without repeating its text.
  const applyStatus = (update: StatusUpdate) => {
    setStatusSteps(prev => {
      const index = prev.findIndex(step => step.id === update.id)
      if (index === -1) return [...prev, update]
      const next = [...prev]
      next[index] = {
        ...next[index],
        ...update,
        label: update.label || next[index].label,
      }
      return next
    })
  }

  // Companies this user may chat with (permission-gated, gails first).
  const companyOptions: Company[] = [
    ...(hasPermission(user?.permissionIds, CHAT_GAILS_PERMISSION_ID)
      ? (['gails'] as Company[])
      : []),
    ...(hasPermission(user?.permissionIds, CHAT_TBF_PERMISSION_ID)
      ? (['tbf'] as Company[])
      : []),
  ]
  const activeCompany = company ?? companyOptions[0] ?? null

  const handleChatSelect = async (selectedChatId: string) => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Chat changed')
    }

    // Remove current chat if it's empty (no messages)
    if (messages.length === 0 && sidebarRef.current) {
      sidebarRef.current.removeEmptyChatIfExists(chatId)
    }

    try {
      const chatData = await getChat(selectedChatId)
      setChatId(selectedChatId)
      setMessages(chatData.messages)
      setStatusSteps([])
      // Restore the chat's company, if it is one this user may access.
      if (chatData.company && companyOptions.includes(chatData.company)) {
        setCompany(chatData.company)
      }
      setInitialMessage('')
      navigate(`/chats/${selectedChatId}`)
      setIsSidebarOpen(false)
    } catch (error) {
      console.error('Error loading chat:', error)
      showError('Could not open that chat.')
    } finally {
      // Whether or not it loaded, stop waiting: a chat that 404s must not leave
      // the page spinning forever.
      setIsLoadingInitialChat(false)
    }
  }

  // Drive the colour theme from the selected company. The attribute goes on
  // <html> rather than the chat wrapper because toasts and the settings modal
  // render through a portal into document.body, outside this tree.
  useEffect(() => {
    const root = document.documentElement
    if (activeCompany) {
      root.setAttribute('data-company', activeCompany)
    } else {
      root.removeAttribute('data-company')
    }
    return () => root.removeAttribute('data-company')
  }, [activeCompany])

  // Load the chat named in the URL — but only once the user's permissions have
  // resolved.
  //
  // This used to run on the first render, while auth was still loading and
  // `user` was null. `companyOptions` was therefore empty, the chat's own
  // company failed the access check below and was dropped, and `setChatId` then
  // made `urlChatId === chatId` so this never ran again. Reloading a TBF chat
  // left the composer and the theme showing Gail's for good, while the backend
  // kept answering as TBF — the chat's stored company is authoritative there.
  useEffect(() => {
    if (status !== AuthStatus.AUTHENTICATED) return
    if (urlChatId && urlChatId !== chatId) {
      handleChatSelect(urlChatId)
    }
  }, [urlChatId, chatId, status])

  if (status === AuthStatus.LOADING) {
    return <Spinner text="Loading your account..." />
  }
  if (status === AuthStatus.UNAUTHENTICATED || !user) {
    return <Navigate to="/login" />
  }
  if (isLoadingInitialChat) {
    return <Spinner text="Loading chat..." />
  }

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false)
  }

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true)
  }

  const fetchChatResponse = async (currentMessages: MessageData[]) => {
    setIsWaiting(true)
    setStatusSteps([])
    const source = createCancelTokenSource()
    setCancelTokenSource(source)

    // Filter out error messages before sending to the API
    const messagesToSend = currentMessages.filter(
      message => message.role !== 'error'
    )

    // Create a message ID for the bot's response
    let hasAddedMessage = false

    let newResponseId: string | null = null

    // A turn may report an error and still have streamed part of an answer, so
    // the two are tracked separately rather than as one success/failure flag.
    let sawError = false

    const appendError = (payload: StreamErrorPayload) => {
      setIsWaiting(false)
      setStatusSteps([])
      setMessages(prevMessages => {
        const last = prevMessages[prevMessages.length - 1]
        if (last && last.role === 'error') return prevMessages
        return [
          ...prevMessages,
          {
            id: uuidv4(),
            role: 'error',
            content: payload.error,
            timestamp: Date.now() / 1000,
            errorCode: payload.code,
            retryable: payload.retryable,
          },
        ]
      })
    }

    try {
      const handleNewResponseId = (responseId: string) => {
        newResponseId = responseId
      }

      // Function to handle streaming updates
      const handleStreamUpdate = (chunk: string) => {
        if (!newResponseId) {
          // Text before the message id means the stream is malformed; say so
          // rather than dropping the content on the floor.
          appendError({
            code: 'unknown',
            error:
              'BreadGPT sent a response we could not attach to this chat. Please try again.',
            retryable: true,
            detail: 'response_text received before message_id',
          })
          return
        }
        // For the first chunk, create a new message
        if (!hasAddedMessage && chunk.trim()) {
          hasAddedMessage = true
          setIsWaiting(false)
          setStatusSteps([])

          const botResponse: MessageData = {
            id: newResponseId,
            role: 'assistant',
            content: chunk,
            timestamp: Date.now() / 1000,
            streamingComplete: false,
          }

          setMessages(prevMessages => [...prevMessages, botResponse])
        }
        // For subsequent chunks, update the existing message
        else if (hasAddedMessage) {
          setMessages(prevMessages => {
            return prevMessages.map(msg =>
              msg.id === newResponseId
                ? { ...msg, content: msg.content + chunk }
                : msg
            )
          })
        }
      }

      // Function to handle document references
      const handleDocumentsReceived = (documents: DocumentReference[]) => {
        setMessages(prevMessages => {
          return prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, documents } : msg
          )
        })
      }

      // Files the agent generated for download. They arrive with the message
      // and are held only in this tab — see GeneratedFile.
      const handleFilesReceived = (files: GeneratedFile[]) => {
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, files } : msg
          )
        )
      }

      // Function to handle DAX queries the analytics agent ran
      const handleDaxQueriesReceived = (daxQueries: DaxQuery[]) => {
        setMessages(prevMessages => {
          return prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, daxQueries } : msg
          )
        })
      }

      // Every failure — agent-side, proxy-side or network — arrives here as a
      // classified payload, so the user always gets a specific sentence and a
      // retry when retrying can actually help.
      const handleStreamError = (payload: StreamErrorPayload) => {
        sawError = true
        if (payload.detail) {
          console.error(`Stream error [${payload.code}]:`, payload.detail)
        }
        // Mark a half-written answer as finished before appending the error, so
        // its copy/feedback controls work on what did arrive.
        if (hasAddedMessage) {
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === newResponseId
                ? { ...msg, streamingComplete: true }
                : msg
            )
          )
        }
        appendError(payload)
      }

      await getChatResponse(
        messagesToSend,
        user.email,
        chatId,
        handleStreamUpdate,
        handleDocumentsReceived,
        handleNewResponseId,
        source,
        handleDaxQueriesReceived,
        activeCompany ?? undefined,
        handleStreamError,
        applyStatus,
        handleFilesReceived
      )

      // Mark streaming as complete
      if (hasAddedMessage) {
        setMessages(prevMessages => {
          return prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, streamingComplete: true } : msg
          )
        })

        // Navigate to specific chat URL after first response is complete
        navigate(`/chats/${chatId}`)

        // Reload chats overview after response is completed
        if (sidebarRef.current) {
          sidebarRef.current.reloadChats()
        }
      }

      // Nothing streamed and nothing reported: clear the loading state rather
      // than leaving a spinner running forever.
      if (!hasAddedMessage && !sawError) {
        setIsWaiting(false)
        setStatusSteps([])
      }
    } catch (error) {
      // getChatResponse reports failures through onError; anything reaching here
      // is unexpected (or a cancellation, which is not a failure).
      if (axios.isCancel(error)) {
        setIsWaiting(false)
        setStatusSteps([])
      } else {
        console.error('Error fetching response:', error)
        appendError({
          code: 'unknown',
          error:
            'Something went wrong while generating a response. Please try again.',
          retryable: true,
          detail: error instanceof Error ? error.message : String(error),
        })
      }
    } finally {
      setIsWaiting(false)
      setCancelTokenSource(null)
    }
  }

  /**
   * Retry the last question.
   *
   * The failed attempt is replaced, not added to — errors never stack up, the
   * way they do not in Claude or ChatGPT. Everything after the last question is
   * dropped: the error, and any half-written answer that preceded it, since a
   * fresh attempt will produce its own. Sending the truncated list also rewrites
   * the stored chat, so the failure disappears from history once it succeeds.
   */
  const handleRetry = () => {
    if (isWaiting) return

    const lastUserIndex = messages.map(m => m.role).lastIndexOf('user')
    if (lastUserIndex === -1) return
    const upToQuestion = messages.slice(0, lastUserIndex + 1)

    setMessages(upToQuestion)
    fetchChatResponse(upToQuestion)
  }

  const handleCancelRequest = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Request canceled by user')
    }
  }

  const handleSendMessage = (content: string) => {
    const userMessage: MessageData = {
      id: uuidv4(),
      role: 'user',
      content,
      timestamp: Date.now() / 1000,
    }

    const updatedMessages = [...messages, userMessage]
    setInitialMessage('')
    setMessages(updatedMessages)
    fetchChatResponse(updatedMessages)
  }

  const handleNewChat = () => {
    // If we're already on an empty chat, don't create a new one
    setIsSidebarOpen(false)
    if (messages.length === 0) {
      // Just focus the input to indicate the action was received
      setFocusTrigger(prev => prev + 1)
      return
    }

    if (cancelTokenSource) {
      cancelTokenSource.cancel('New chat started')
    }

    const newChatId = uuidv4()
    setMessages([])
    setStatusSteps([])
    setChatId(newChatId)
    setFocusTrigger(prev => prev + 1)
    navigate('/chats') // Navigate to /chats instead of /chats/{newChatId}
  }

  const handleExampleClick = (example: string) => {
    setInitialMessage(example)
  }

  const handleSignOut = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('User signed out')
    }
    logout()
  }

  return (
    <div className="chat-page">
      <div className="chat-page__container">
        <TopBar
          onToggleSidebar={handleOpenSidebar}
          isSidebarOpen={isSidebarOpen}
        />

        <Sidebar
          ref={sidebarRef}
          isOpen={isSidebarOpen}
          onClose={handleCloseSidebar}
          onNewChat={handleNewChat}
          onSignOut={handleSignOut}
          onChatSelect={handleChatSelect}
          user={user}
          currentChatId={chatId}
          isNewChatRoute={!urlChatId}
          onChatCreated={chatId => {
            // Add new chat to sidebar when created
            console.log('Chat created:', chatId)
          }}
          onChatDeleted={deletedChatId => {
            // Handle chat deletion - open new chat if current chat was deleted
            if (deletedChatId === chatId) {
              handleNewChat()
            }
          }}
        />

        <div className="chat-page__main">
          <NewsflashBanner />
          <div className="chat-page__content">
            {messages.length === 0 ? (
              <Welcome
                onExampleClick={handleExampleClick}
                company={activeCompany}
                permissionIds={user?.permissionIds}
              />
            ) : (
              <MessageList
                messages={messages}
                isWaiting={isWaiting}
                user={user}
                chatId={chatId}
                statusSteps={statusSteps}
                onRetry={handleRetry}
              />
            )}
          </div>

          {companyOptions.length === 0 ? (
            <div className="chat-page__no-access">
              You do not have access to the BreadGPT assistant yet. Ask an
              administrator to grant you a role in Users &amp; Roles.
            </div>
          ) : (
            <ChatInput
              onSendMessage={handleSendMessage}
              onCancelRequest={handleCancelRequest}
              isWaiting={isWaiting}
              initialMessage={initialMessage}
              focusTrigger={focusTrigger}
              company={activeCompany ?? undefined}
              companyOptions={companyOptions}
              onCompanyChange={setCompany}
              // Company is chosen for a new chat, then locked once it has messages.
              companyLocked={messages.length > 0}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatPage
